import { Injectable } from '@angular/core';
import { AddonKey, CommonField, CustomField, Field, FieldType, Status } from '@vendasta/sales-orders';
import {
  AddonDropDownFormSectionData,
  CommonFieldIdsInterface,
  CommonFormData,
  CommonFormFieldInterface,
  CommonFormSectionInterface,
  CustomFieldsAnswer,
  CustomFieldsAnswers,
  OrderFormFieldOptionsType,
  OrderFormInterface,
  ProductInfoInterface,
  ProductOrderFormSectionData,
} from './interface';

import { UntypedFormGroup } from '@angular/forms';
import { Option as InputOption } from '@vendasta/forms';
import { DropDownFormSectionData } from '../dropdown-form-section/dropdown-form-section-data';
import { BUSINESS_USER, ControlType, DROP_DOWN, FieldBase } from '../fields/field-base';
import { CheckboxField } from '../fields/field-checkbox';
import { DropdownField, Option } from '../fields/field-dropdown';
import { FileUploadGroupField, FileUploadGroupFieldOptions } from '../fields/field-fileuploadgroup';
import { TextareaField } from '../fields/field-textarea';
import { TextboxField } from '../fields/field-textbox';
import { User, VBCUserField, VBCUserFieldOptions } from '../fields/field-vbcuser';
import { OrderFormOptions } from './order-form.component';

const fieldIdToTranslationKeyMap = {
  businessName: 'FRONTEND.STORE.ORDER_FORM.LABELS.BUSINESS_NAME',
  businessAddress: 'FRONTEND.STORE.ORDER_FORM.LABELS.BUSINESS_ADDRESS',
  businessPhoneNumber: 'FRONTEND.STORE.ORDER_FORM.LABELS.BUSINESS_PHONE_NUMBER',
  businessWebsite: 'FRONTEND.STORE.ORDER_FORM.LABELS.BUSINESS_WEBSITE',
  businessAccountGroupId: 'FRONTEND.STORE.ORDER_FORM.LABELS.BUSINESS_ID',
  salespersonName: 'FRONTEND.STORE.ORDER_FORM.LABELS.SALESPERSON_NAME',
  salespersonPhoneNumber: 'FRONTEND.STORE.ORDER_FORM.LABELS.SALESPERSON_PHONE_NUMBER',
  salespersonEmail: 'FRONTEND.STORE.ORDER_FORM.LABELS.SALESPERSON_EMAIL',
  contactName: 'FRONTEND.STORE.ORDER_FORM.LABELS.CONTACT_NAME',
  contactPhoneNumber: 'FRONTEND.STORE.ORDER_FORM.LABELS.CONTACT_PHONE_NUMBER',
  contactEmail: 'FRONTEND.STORE.ORDER_FORM.LABELS.CONTACT_EMAIL',
};

@Injectable()
export class OrderFormService {
  static camelToSnakeCaseMap = {
    contactName: 'contact_name',
    contactEmail: 'contact_email',
    contactPhoneNumber: 'contact_phone_number',
    businessAccountGroupId: 'business_account_group_id',
    businessName: 'business_name',
    businessAddress: 'business_address',
    businessPhoneNumber: 'business_phone_number',
    businessWebsite: 'business_website',
    salespersonName: 'salesperson_name',
    salespersonEmail: 'salesperson_email',
    salespersonPhoneNumber: 'salesperson_phone_number',
  };

  static fieldIdToTranslationKeyMap = fieldIdToTranslationKeyMap;

  static updatedFieldIdToTranslationKeyMap = {
    ...fieldIdToTranslationKeyMap,
    contactName: 'FRONTEND.STORE.ORDER_FORM.UPDATED_LABELS.NAME',
    contactPhoneNumber: 'FRONTEND.STORE.ORDER_FORM.UPDATED_LABELS.PHONE_NUMBER',
    contactEmail: 'FRONTEND.STORE.ORDER_FORM.UPDATED_LABELS.EMAIL',
    salespersonName: 'FRONTEND.STORE.ORDER_FORM.UPDATED_LABELS.NAME',
    salespersonPhoneNumber: 'FRONTEND.STORE.ORDER_FORM.UPDATED_LABELS.PHONE_NUMBER',
    salespersonEmail: 'FRONTEND.STORE.ORDER_FORM.UPDATED_LABELS.EMAIL',
  };

  static fieldIdToEnglishNameMap = {
    business_name: 'Business Name',
    business_address: 'Business Address',
    business_phone_number: 'Business Phone Number',
    business_website: 'Business Website',
    business_account_group_id: 'Business ID',
    salesperson_name: 'Salesperson Name',
    salesperson_phone_number: 'Salesperson Phone Number',
    salesperson_email: 'Salesperson Email',
    contact_name: 'Contact Name',
    contact_phone_number: 'Contact Phone Number',
    contact_email: 'Contact Email',
  };

  private static extractRequiredCommonFieldIds(orderForm: OrderFormInterface): string[] {
    if (!orderForm || !orderForm.commonFormRequiredFields) {
      return [];
    }
    return Object.keys(orderForm.commonFormRequiredFields).filter((k) => orderForm.commonFormRequiredFields[k]);
  }

  private static buildCommonFieldIds(orderForms: OrderFormInterface[]): CommonFieldIdsInterface[] {
    const commonFieldIdsMap: Map<string, CommonFieldIdsInterface> = new Map<string, CommonFieldIdsInterface>();
    for (const key in OrderFormService.camelToSnakeCaseMap) {
      if (key) {
        commonFieldIdsMap.set(key, {
          fieldId: key,
          required: false,
        });
      }
    }
    (orderForms || []).map((orderForm) => {
      const requiredCommonFieldIds = this.extractRequiredCommonFieldIds(orderForm);
      requiredCommonFieldIds.map((fieldId) => {
        const commonFieldId = commonFieldIdsMap.get(fieldId);
        commonFieldId.required = true;
        commonFieldIdsMap.set(fieldId, commonFieldId);
      });
    });
    return Array.from(commonFieldIdsMap.values());
  }

  private static fillCommonFormSections(
    fieldIds: CommonFieldIdsInterface[],
    commonData: CommonFormData,
    options: OrderFormOptions,
    contactIdDescription: string,
    contactIdLabel: string,
  ): CommonFormSectionInterface[] {
    const userOptions = commonData?.user_options || [];
    const commonFormSections = OrderFormService.buildCommonOrderFormFields(
      fieldIds,
      options,
      userOptions,
      contactIdDescription,
      contactIdLabel,
    );
    return commonFormSections.map((section) => ({ ...section, populatedData: commonData }));
  }

  private static buildCommonOrderFormFields(
    fieldIds: CommonFieldIdsInterface[],
    options: OrderFormOptions,
    users: User[],
    contactIdDescription?: string,
    contactIdLabel?: string,
  ): CommonFormSectionInterface[] {
    const businessSection: CommonFormSectionInterface = {
      sectionName: 'FRONTEND.STORE.ORDER_FORM.LABELS.BUSINESS_SECTION_TITLE',
      fields: [],
    };
    const salespersonSection: CommonFormSectionInterface = {
      sectionName: 'FRONTEND.STORE.ORDER_FORM.LABELS.SALESPERSON_SECTION_TITLE',
      fields: [],
    };
    const contactSection: CommonFormSectionInterface = {
      sectionName: 'FRONTEND.STORE.ORDER_FORM.LABELS.CONTACT_SECTION_TITLE',
      fields: [],
    };

    const translateKeyMap = OrderFormService.fieldIdToTranslationKeyMap;

    for (const field of fieldIds) {
      const commonFormField: CommonFormFieldInterface = new TextboxField({
        id: OrderFormService.camelToSnakeCaseMap[field.fieldId],
        label: translateKeyMap[field.fieldId] || '',
        textboxType: 'text',
        hidden: !field.required,
      });
      commonFormField.displayOnly = commonFormField.id === 'business_account_group_id';
      commonFormField.disabled = options.readOnly;
      if (!options.bypassRequiredQuestions) {
        commonFormField.required = field.required;
      }
      if (field.fieldId.startsWith('contact')) {
        contactSection.fields.push(commonFormField);
      }
      if (field.fieldId.startsWith('business')) {
        businessSection.fields.push(commonFormField);
      }
      if (field.fieldId.startsWith('salesperson')) {
        if (!options.showOfficeUseQuestions) {
          commonFormField.hidden = true;
        }
        salespersonSection.fields.push(commonFormField);
      }
    }

    // Add a VBC user control field to select the desired user
    // Default the value to the other controls
    if (contactSection.fields.length > 0 && users?.length > 1) {
      const fieldConfig = {
        id: 'contact_id',
        description: contactIdDescription,
        label: contactIdLabel,
        users: users,
        disabled: options.readOnly,
      } as VBCUserFieldOptions;
      contactSection.fields.unshift(new VBCUserField(fieldConfig));
    }

    return [businessSection, contactSection, salespersonSection].filter((section) =>
      section.fields ? section.fields.length > 0 : false,
    );
  }

  private static buildOrderFormSectionData(parentForm: UntypedFormGroup): AddonDropDownFormSectionData {
    const section = new AddonDropDownFormSectionData();
    section.displayAutoTitle = true;
    section.descriptionText = '';
    section.displayAutoDescription = false;
    section.parentForm = parentForm;
    return section;
  }

  private static getValueFromCustomFieldsAnswer(
    customFieldsAnswer: CustomFieldsAnswer,
    fieldClass: any,
    allowMultiples: boolean,
  ): any {
    let answer;
    try {
      answer = JSON.parse(customFieldsAnswer.answer);
    } catch {
      answer = customFieldsAnswer.answer;
    }

    let value = answer;
    if (!!answer && (fieldClass === DropdownField || fieldClass === VBCUserField)) {
      if (allowMultiples) {
        if (!Array.isArray(answer)) {
          answer = [answer];
        }
        value = answer.map((a) => {
          if ((a as Option).value !== undefined) {
            return a.value;
          } else {
            return a;
          }
        });
      } else {
        if ((answer as Option).value !== undefined) {
          value = answer.value;
        }
      }
    }
    return value;
  }

  private static buildCustomOrderFormFields(
    customFields: OrderFormFieldOptionsType[],
    fileUploadUrl: string,
    users: User[],
    answers: CustomFieldsAnswer[],
    options: OrderFormOptions,
    uniqueId: string,
  ): FieldBase<any>[] {
    const fieldTypeMap = {
      text: TextboxField,
      textbox: TextboxField,
      dropdown: DropdownField,
      checkbox: CheckboxField,
      file: FileUploadGroupField,
      textarea: TextareaField,
      vbcuser: VBCUserField,
    };
    return customFields
      .filter((field) => {
        return fieldTypeMap[field.type];
      })
      .map((field) => {
        const fieldClass = fieldTypeMap[field.type];
        const result = (() => {
          switch (fieldClass) {
            case VBCUserField: {
              const fieldConfig = {
                description: field.description,
                id: field.id,
                required: field.required,
                type: field.type,
                label: field.label,
                forOfficeUseOnly: field.forOfficeUseOnly,
                officeEditableOnly: field.officeEditableOnly,
                hidden: field.hidden,
                users: users,
              } as VBCUserFieldOptions;
              return new VBCUserField(fieldConfig);
            }
            case FileUploadGroupField: {
              const uploadField = field as FileUploadGroupFieldOptions;
              if (!uploadField.uploadUrl) {
                uploadField.uploadUrl = fileUploadUrl;
              }
              return new FileUploadGroupField(uploadField);
            }
            default:
              return new fieldClass(field);
          }
        })();
        if (answers) {
          const ca = answers.find((a) => a.fieldId === field.id);
          if (ca) {
            result.value = OrderFormService.getValueFromCustomFieldsAnswer(ca, fieldClass, result.allowMultiples);
            if (ca.displayOnly) {
              result.displayOnly = ca.displayOnly;
            }
          }
        }
        result.id = OrderFormService.encodeCustomFieldId(field.id, uniqueId);
        result.disabled = options.readOnly ? options.readOnly : result.disabled;

        if (!options.showOfficeUseQuestions) {
          if (result.forOfficeUseOnly) {
            result.hidden = true;
          }
          if (result.officeEditableOnly) {
            result.disabled = true;
            // hide the question if it doesn't have a value, but always show checkboxes
            const hasValue = Array.isArray(result.value) ? result.value.length > 0 : !!result.value;
            result.hidden = !hasValue && !(fieldClass === CheckboxField);
          }
        }
        return result;
      });
  }

  // Different products can have order form fields with the same fieldID, which will cause problem.
  // Please refer to https://github.com/vendasta/business-center-client/pull/1002
  // We will prefix the fieldID with a uniqueId, and take it off before we save the order form into session storage
  private static encodeCustomFieldId(fieldId: string, uniqueId: string): string {
    return `${uniqueId}#${fieldId}`;
  }

  private static decodeCustomFieldId(fieldId: string): string {
    return fieldId.replace(new RegExp('(.*#)'), '');
  }

  private static mapFieldType(fieldType: ControlType): FieldType {
    switch (fieldType) {
      case 'textbox':
        return FieldType.TEXT;
      case 'textarea':
        return FieldType.TEXTAREA;
      case 'checkbox':
        return FieldType.CHECKBOX;
      case 'dropdown':
        return FieldType.DROPDOWN;
      case 'file':
        return FieldType.FILE;
      case 'vbcuser':
        return FieldType.VBCUSER;
      default:
        return FieldType.NOT_SPECIFIED;
    }
  }

  private static getOptionFromValue(value: string, options: (Option | InputOption)[]): Option {
    let foundOption = options.find((o) => o.value === value);
    const inputOption = foundOption as InputOption;
    if (!!foundOption && inputOption.name !== undefined) {
      foundOption = { value: inputOption.value, label: inputOption.name };
    }
    return foundOption as Option;
  }

  private static getAnswerFromFieldBase(fieldBase: FieldBase<any>): any {
    if (!fieldBase.value || (fieldBase.controlType !== DROP_DOWN && fieldBase.controlType !== BUSINESS_USER)) {
      return fieldBase.value;
    }

    const dropdownField = fieldBase as DropdownField;
    if (dropdownField.allowMultiples) {
      const fieldValues = dropdownField.value as string[];
      const selectedOptions = [];
      fieldValues.forEach((fieldValue) => {
        const selectedOption = OrderFormService.getOptionFromValue(fieldValue, dropdownField.options);
        if (selectedOption) {
          selectedOptions.push(selectedOption);
        }
      });
      return selectedOptions;
    }
    return OrderFormService.getOptionFromValue(dropdownField.value as string, dropdownField.options);
  }

  private static orderFieldToSalesOrderField(fieldBase: FieldBase<any>): Field {
    const answer = OrderFormService.getAnswerFromFieldBase(fieldBase);
    return new Field({
      fieldId: OrderFormService.decodeCustomFieldId(fieldBase.id),
      fieldType: OrderFormService.mapFieldType(fieldBase.controlType),
      label: fieldBase.label,
      description: fieldBase.description || '',
      prefix: fieldBase.prefix || '',
      suffix: fieldBase.suffix || '',
      regexValidator: fieldBase.regexValidator || '',
      regexErrorMessage: fieldBase.regexErrorMessage || '',
      answer: answer ? JSON.stringify(answer) : 'null',
    });
  }

  private static productOrderFormToCustomFields(orderFormData: ProductOrderFormSectionData): CustomField[] {
    const primaryCustomField = new CustomField();
    primaryCustomField.productId = orderFormData.productId;
    primaryCustomField.fields = orderFormData.primarySection
      ? orderFormData.primarySection.fields.map((field) => this.orderFieldToSalesOrderField(field))
      : [];
    const secondaryCustomFields = orderFormData.subsections.map((subsection) => {
      const customField = new CustomField();
      if (subsection.addonId) {
        customField.addonKey = new AddonKey({
          appId: subsection.productId,
          addonId: subsection.addonId,
        });
      } else {
        customField.productId = orderFormData.productId;
      }
      customField.fields = subsection.fields.map((field) => this.orderFieldToSalesOrderField(field));
      return customField;
    });
    return [].concat(primaryCustomField, secondaryCustomFields);
  }

  public static getCommonFormSections(
    orderForms: OrderFormInterface[],
    commonData: CommonFormData,
    options: OrderFormOptions,
    contactIdDescription: string,
    contactIdLabel: string,
  ): CommonFormSectionInterface[] {
    const fieldIds = OrderFormService.buildCommonFieldIds(orderForms);
    return OrderFormService.fillCommonFormSections(fieldIds, commonData, options, contactIdDescription, contactIdLabel);
  }

  public static getCustomFormSections(
    businessId: string,
    orderForms: OrderFormInterface[],
    products: ProductInfoInterface[],
    parentForm: UntypedFormGroup,
    fileUploadUrl: string,
    users: User[],
    answers: CustomFieldsAnswers[],
    options: OrderFormOptions,
  ): ProductOrderFormSectionData[] {
    const formSections = (products || [])
      .map((product) => {
        const subsections: AddonDropDownFormSectionData[] = [];
        (product.addonInfo || []).map((addon) => {
          const addonOrderForms = (orderForms || []).filter((o) => o.addonId === addon.addonId);
          const addonAnswers: CustomFieldsAnswers[] = (answers || []).filter(
            (a) =>
              a.addonKey &&
              a.addonKey.appId === product.productId &&
              a.addonKey.addonId === addon.addonId &&
              a.productID === product.productId,
          );
          addonOrderForms.map((addonOrderForm, index) => {
            if (addonOrderForm && addonOrderForm.customFields && addonOrderForm.customFields.length > 0) {
              const addonSection = OrderFormService.buildOrderFormSectionData(parentForm);
              const addonAnswersForThisSpecificFormIndex =
                addonAnswers.length > index ? addonAnswers[index].customFieldsAnswers : [];
              const formFields = OrderFormService.buildCustomOrderFormFields(
                addonOrderForm.customFields,
                fileUploadUrl,
                users,
                addonAnswersForThisSpecificFormIndex,
                options,
                `${addonOrderForm.addonId}:${index}`,
              );
              addonSection.expandable = true;
              addonSection.titleText = addon.name;
              addonSection.fields = formFields;
              addonSection.productId = product.productId;
              addonSection.addonId = addon.addonId;
              subsections.push(addonSection);
            }
          });
        });

        const foundOrderForms = (orderForms || []).reduce((orderFormsFound, o) => {
          if (o.appId === product.productId && !o.addonId) {
            orderFormsFound.push(o);
          }
          return orderFormsFound;
        }, []);
        const [primaryOrder, ...otherOrdersForSameProduct] = foundOrderForms;
        const productHasOrderForm = primaryOrder && primaryOrder.customFields && primaryOrder.customFields.length > 0;
        if (subsections.length > 0 || productHasOrderForm || primaryOrder?.footerText) {
          const sectionData = new ProductOrderFormSectionData();
          sectionData.businessId = businessId;
          sectionData.productId = product.productId;
          sectionData.parentForm = parentForm;
          sectionData.titleText = product.name;
          sectionData.subtitleText = product.tagline;
          sectionData.descriptionText = ''; // TODO: build the description string to show which common data this form uses
          sectionData.iconUrl = product.icon;
          if (primaryOrder && primaryOrder.footerText) {
            sectionData.footerText = primaryOrder.footerText;
          }

          if (productHasOrderForm) {
            const section = OrderFormService.buildOrderFormSectionData(parentForm);
            section.titleText = product.name;
            let productAnswers: CustomFieldsAnswers[] = [];
            if (answers) {
              productAnswers = answers.filter((a) => !a.addonKey && a.productID === product.productId);
            }
            const [firstProductAnswers, ...otherProductAnswers] = productAnswers;
            section.fields = OrderFormService.buildCustomOrderFormFields(
              primaryOrder.customFields,
              fileUploadUrl,
              users,
              firstProductAnswers ? firstProductAnswers.customFieldsAnswers : [],
              options,
              primaryOrder.appId,
            );
            sectionData.primarySection = section;

            otherOrdersForSameProduct.forEach((_, index) => {
              /**
               * Clone the same section to avoid child component from modifying all as if these are all the top section.
               * All sections need to be re-created or else they will have the same id and send repeated data as well.
               */
              const dupeSection = { ...section };
              const answersForSpecificForm =
                otherProductAnswers.length > index ? otherProductAnswers[index].customFieldsAnswers : [];
              dupeSection.fields = OrderFormService.buildCustomOrderFormFields(
                primaryOrder.customFields,
                fileUploadUrl,
                users,
                answersForSpecificForm,
                options,
                `${primaryOrder.appId}:${index}`,
              );

              subsections.unshift(dupeSection);
            });
          }

          sectionData.subsections = subsections;
          return sectionData;
        }
        return null;
      })
      .filter((s) => !!s);

    return formSections;
  }

  public static getExtraFieldsSection(
    extraFields: OrderFormFieldOptionsType[],
    parentForm: UntypedFormGroup,
    fileUploadUrl: string,
    users: User[],
    answers: CustomFieldsAnswer[],
    options: OrderFormOptions,
  ): DropDownFormSectionData {
    if (!extraFields || extraFields.length < 1) {
      return null;
    }
    const section = OrderFormService.buildOrderFormSectionData(parentForm);
    section.fields = OrderFormService.buildCustomOrderFormFields(
      extraFields,
      fileUploadUrl,
      users,
      answers,
      options,
      'extraFields',
    );
    return section;
  }

  public static getCustomFieldsData(customFormSections: ProductOrderFormSectionData[]): CustomField[] {
    if (!customFormSections) {
      return [];
    }
    const customFormData = customFormSections.reduce((acc, orderFormData) => {
      acc = acc.concat(OrderFormService.productOrderFormToCustomFields(orderFormData));
      return acc;
    }, []);
    return customFormData;
  }

  public static getCommonFieldsData(commonFormSections: CommonFormSectionInterface[]): CommonField[] {
    let commonFields: CommonField[] = [];
    if (commonFormSections) {
      commonFields = commonFormSections.reduce((acc, group) => {
        const fields = group.fields.map((singleField) => {
          const field = OrderFormService.orderFieldToSalesOrderField(singleField);
          if (field.fieldId) {
            field.label = OrderFormService.fieldIdToEnglishNameMap[field.fieldId] || field.label;
            return new CommonField({
              field: field,
              productIds: singleField.productIds,
              addonKeys: singleField.addonKeys,
            });
          }
        });
        return acc.concat(fields);
      }, []);
    }
    return commonFields;
  }

  public static getExtraFieldsData(extraFieldsSection: DropDownFormSectionData): Field[] {
    let extraFieldsData: Field[] = [];
    if (extraFieldsSection) {
      extraFieldsData = extraFieldsSection.fields.map((field) => OrderFormService.orderFieldToSalesOrderField(field));
    }
    return extraFieldsData;
  }

  public static containsFieldsThatMatch(
    customFormSections: ProductOrderFormSectionData[],
    matcher: (f: FieldBase<any>) => boolean,
  ): boolean {
    if (!customFormSections) {
      return false;
    }
    return customFormSections.some((sec) => {
      if (!sec) {
        return false;
      }
      let primaryMatches, subSectionMatches: boolean;
      if (sec.primarySection && sec.primarySection.fields) {
        primaryMatches = sec.primarySection.fields.some((f) => matcher(f));
      }
      if (sec.subsections) {
        subSectionMatches = sec.subsections.some((subSec) => {
          if (subSec && subSec.fields) {
            return subSec.fields.some((f) => matcher(f));
          }
        });
      }
      return primaryMatches || subSectionMatches;
    });
  }

  public static shouldHideCustomFields(status: Status): boolean {
    return [
      null,
      undefined,
      Status.DRAFTED,
      Status.SUBMITTED,
      Status.RESUBMITTED,
      Status.APPROVED,
      Status.DECLINED,
      Status.CANCELLATION_REQUESTED,
      Status.SCHEDULED_ACTIVATION,
    ].includes(status);
  }
}
